import React, {useEffect, useState} from 'react'
import {useForm, Controller} from 'react-hook-form'
import {useAuth0} from '@auth0/auth0-react'
import {toast} from 'react-toastify'
import {Checkbox} from '@thryvlabs/maverick'
import {Input} from '../../accountManagement/shared/Input'
import {Button} from '../../accountManagement/shared/Button'
import Modal from '../../accountManagement/shared/Modal'
import {useAuthClient} from '../../utils/use-auth-client'
import {PillTagList, CompatiblePlansList} from './PillTagList'
import './coupon-select.css'

const FONT = "'Inter', 'Helvetica Neue', Arial, sans-serif"

const ORANGE_PRIMARY = {backgroundColor: '#FF5000', borderColor: '#FF5000'}
const ORANGE_SECONDARY = {borderColor: '#FF5000', color: '#FF5000'}

const CB_INPUT = {width: '20px', height: '20px', minWidth: '20px'}
const CB_LABEL = {
  fontFamily: FONT,
  color: '#374151',
  margin: 0,
  fontSize: '14px',
  fontWeight: 500,
}

const TODAY = new Date().toISOString().split('T')[0]

const sectionLabel = {
  fontFamily: FONT,
  fontWeight: 600,
  fontSize: '14px',
  color: '#111827',
  marginTop: '24px',
  marginBottom: '10px',
  paddingBottom: '6px',
  borderBottom: '1px solid #ededf2',
}

const auditFieldLabel = {
  fontFamily: FONT,
  fontSize: '12px',
  fontWeight: 500,
  color: '#374151',
  margin: '0 0 3px',
}

const auditFieldValue = {
  fontFamily: FONT,
  fontSize: '13px',
  fontWeight: 400,
  color: '#111827',
  margin: 0,
}

function AuditField({label, value}) {
  return (
    <div>
      <p style={auditFieldLabel}>{label}</p>
      <p style={auditFieldValue}>{value || '—'}</p>
    </div>
  )
}

function EditCouponModal({coupon, onClose, onSaved}) {
  const {user} = useAuth0()
  const client = useAuthClient()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [allRoles, setAllRoles] = useState([])
  // const [clientGroupOptions, setClientGroupOptions] = useState([])
  const [clientGroup, setClientGroup] = useState('None')
  const [confirmClose, setConfirmClose] = useState(false)
  const [auditInfo, setAuditInfo] = useState({
    createdBy: '',
    createdDate: '',
    modifiedBy: '',
    modifiedDate: '',
  })

  const {
    control,
    handleSubmit,
    reset,
    // setValue,
    watch,
    getValues,
    trigger,
    formState: {errors, isDirty, dirtyFields},
  } = useForm({
    defaultValues: {
      couponDesc: '',
      effectiveDate: '',
      endDate: '',
      defaultFlag: 'N',
      newClientOnlyFlag: 'N',
      roles: [],
      exclusionDates: [],
      incompatibleCoupons: [],
      compatiblePlans: [],
      bundleConfigurations: null,
    },
    mode: 'onTouched',
    reValidateMode: 'onChange',
  })

  const defaultFlag = watch('defaultFlag') === 'Y'
  const newClientOnlyFlag = watch('newClientOnlyFlag') === 'Y'

  useEffect(() => {
    ;(async () => {
      try {
        const [details, rolesRes] = await Promise.all([
          client(
            `coupons/fetchCoupon/${coupon.country}/${encodeURIComponent(
              coupon.couponCode,
            )}`,
          ),
          client('coupons/roleNames'),
          // client('coupons/clientGroup'),
        ])

        const rawRoles = rolesRes?.data?.[0]?.value || ''
        setAllRoles(
          rawRoles
            .split(',')
            .map(r => r.trim())
            .filter(Boolean),
        )
        // const rawClientGroups = clientGroupRes?.data?.[0]?.value || ''
        // setClientGroupOptions(
        //   rawClientGroups
        //     .split(',')
        //     .map(g => g.trim())
        //     .filter(Boolean),
        // )

        setClientGroup(details.clientGroup || 'None')
        setAuditInfo({
          createdBy: details.createdBy || '',
          createdDate: details.createdDate || '',
          modifiedBy: details.modifiedBy || '',
          modifiedDate: details.modifiedDate || '',
        })

        reset({
          couponDesc: details.couponDesc || '',
          effectiveDate: details.effectiveDate || '',
          endDate: details.endDate || '',
          defaultFlag: details.defaultFlag || 'N',
          newClientOnlyFlag: details.newClientOnlyFlag || 'N',
          roles: (details.compatibleRoles || []).map(r => ({roleName: r})),
          exclusionDates: (details.exclusionDates || []).map(d => ({date: d})),
          incompatibleCoupons: (details.incompatibleCoupons || []).map(c => ({
            incompatibleCoupon: c,
          })),
          compatiblePlans: (details.compatiblePlans || []).map(p => ({
            planCode: p.planCode || '',
            planDesc: p.planDesc || '',
            // newAutoApply: p.newAutoApply || 'N',
            // existingAutoApply: p.existingAutoApply || 'N',
          })),
          bundleConfigurations: details.bundleConfigurations || null,
        })
      } catch {
        toast.error('Failed to load coupon details')
        onClose()
      } finally {
        setLoading(false)
      }
    })()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  async function onSubmit(formData) {
    setSaving(true)
    try {
      await client(
        `coupons/updateCoupon/${coupon.country}/${encodeURIComponent(
          coupon.couponCode,
        )}`,
        {
          method: 'PUT',
          data: {
            country: coupon.country,
            couponCode: coupon.couponCode,
            couponDesc: formData.couponDesc,
            effectiveDate: formData.effectiveDate,
            endDate: formData.endDate,
            // defaultFlag: formData.defaultFlag || 'N',
            // newClientOnlyFlag: formData.newClientOnlyFlag || 'N',
            modifiedBy: user?.email,
            compatibleRoles: formData.roles
              .map(r => r.roleName)
              .filter(r => r && r.trim() !== ''),
            exclusionDates: formData.exclusionDates
              .map(d => d.date)
              .filter(Boolean),
            incompatibleCoupons: formData.incompatibleCoupons
              .map(c => c.incompatibleCoupon)
              .filter(c => c && c.trim() !== ''),
            compatiblePlans: (formData.compatiblePlans || [])
              .filter(p => p.planCode && p.planCode.trim() !== '')
              .map(p => ({
                planCode: p.planCode,
                planDesc: p.planDesc || '',
                // newAutoApply: p.newAutoApply || 'N',
                // existingAutoApply: p.existingAutoApply || 'N',
              })),
          },
        },
      )
      toast.success('Coupon updated successfully')
      onSaved()
    } catch (error) {
      toast.error(error.message)
    } finally {
      setSaving(false)
    }
  }

  const handleClose = () => {
    if (isDirty) {
      setConfirmClose(true)
    } else {
      onClose()
    }
  }

  const footerButtons = (
    <>
      <Button
        variant="secondary"
        style={ORANGE_SECONDARY}
        onClick={handleClose}
      >
        Cancel
      </Button>
      <Button
        variant="primary"
        style={ORANGE_PRIMARY}
        disabled={saving || !isDirty}
        onClick={handleSubmit(onSubmit)}
      >
        {saving ? 'Saving...' : 'Save Changes'}
      </Button>
    </>
  )

  return (
    <Modal
      isOpen
      onClose={handleClose}
      title={`Edit Coupon : ${coupon.couponCode} - ${coupon.country}`}
      width="min(1300px, 94vw)"
      footer={footerButtons}
      footerAlign="right"
    >
      <div className="coupon-form-fields">
        {loading ? (
          <p
            style={{
              fontFamily: FONT,
              fontSize: '14px',
              textAlign: 'center',
              padding: '60px 0',
              color: '#6b7280',
            }}
          >
            Loading coupon details...
          </p>
        ) : (
          <div style={{display: 'flex', flexDirection: 'column', gap: '4px'}}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '16px',
              }}
            >
              <Input
                type="text"
                label="Country"
                value={coupon.country}
                disabled
              />
              <div style={{display: 'flex', gap: '12px'}}>
                <div style={{flex: 1}}>
                  <Controller
                    control={control}
                    name="effectiveDate"
                    rules={{
                      required: 'Effective date is required',
                      validate: value =>
                        !value ||
                        !dirtyFields.effectiveDate ||
                        value >= TODAY ||
                        'Effective date cannot be in the past',
                    }}
                    render={({field}) => (
                      <Input
                        type="date"
                        label="Effective Date"
                        required
                        errorText={errors?.effectiveDate?.message}
                        value={field.value}
                        onChange={e => {
                          field.onChange(e.target.value)
                          if (getValues('endDate')) trigger('endDate')
                        }}
                        onBlur={field.onBlur}
                        name={field.name}
                      />
                    )}
                  />
                </div>
                <div style={{flex: 1}}>
                  <Controller
                    control={control}
                    name="endDate"
                    rules={{
                      required: 'End date is required',
                      validate: value => {
                        const eff = getValues('effectiveDate')
                        if (!eff || !value) return true
                        if (!dirtyFields.effectiveDate && !dirtyFields.endDate)
                          return true
                        return (
                          value > eff || 'End date must be after Effective date'
                        )
                      },
                    }}
                    render={({field}) => (
                      <Input
                        type="date"
                        label="End Date"
                        required
                        errorText={errors?.endDate?.message}
                        value={field.value}
                        onChange={e => {
                          field.onChange(e.target.value)
                          if (getValues('effectiveDate'))
                            trigger('effectiveDate')
                        }}
                        onBlur={field.onBlur}
                        name={field.name}
                      />
                    )}
                  />
                </div>
              </div>
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr 1fr',
                gap: '26px',
                marginTop: '16px',
              }}
            >
              <Input
                type="text"
                label="Coupon Code"
                value={coupon.couponCode}
                disabled
              />
              <Controller
                control={control}
                name="couponDesc"
                rules={{
                  required: 'Coupon description is required',
                  maxLength: {
                    value: 250,
                    message:
                      'Coupon description must be at most 250 characters',
                  },
                }}
                render={({field}) => (
                  <Input
                    type="text"
                    label="Coupon Description"
                    required
                    errorText={errors?.couponDesc?.message}
                    value={field.value}
                    onChange={e => field.onChange(e.target.value)}
                    onBlur={field.onBlur}
                    name={field.name}
                  />
                )}
              />
              <Input
                type="text"
                label="Client Group"
                value={clientGroup}
                disabled
              />
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr 1fr',
                gap: '26px',
                marginTop: '20px',
              }}
            >
              <div style={{pointerEvents: 'none', opacity: 0.55}}>
                <Checkbox
                  hasLabel
                  style={CB_INPUT}
                  name="defaultFlag"
                  label={<label style={CB_LABEL}>Default Flag</label>}
                  isChecked={defaultFlag}
                  onChange={() => {}}
                />
              </div>
              <div style={{pointerEvents: 'none', opacity: 0.55}}>
                <Checkbox
                  hasLabel
                  style={CB_INPUT}
                  name="newClientOnlyFlag"
                  label={<label style={CB_LABEL}>New Client Only Flag</label>}
                  isChecked={newClientOnlyFlag}
                  onChange={() => {}}
                />
              </div>
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr 1fr 1fr',
                gap: '16px',
                marginTop: '20px',
                paddingTop: '16px',
                borderTop: '1px solid #ededf2',
              }}
            >
              <AuditField label="Created By" value={auditInfo.createdBy} />
              <AuditField label="Created Date" value={auditInfo.createdDate} />
              <AuditField label="Modified By" value={auditInfo.modifiedBy} />
              <AuditField
                label="Modified Date"
                value={auditInfo.modifiedDate}
              />
            </div>

            {watch('bundleConfigurations') && (
              <>
                <p style={sectionLabel}>Bundle Configurations</p>
                <p
                  style={{
                    fontFamily: FONT,
                    fontSize: '13px',
                    color: '#6b7280',
                    margin: '0 0 8px',
                  }}
                >
                  Bundle configuration details (read-only)
                </p>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr 1fr',
                    gap: '16px',
                    marginBottom: '12px',
                  }}
                >
                  <Input
                    type="text"
                    label="Bundle Group"
                    value={watch('bundleConfigurations')?.bundleGroup || '—'}
                    disabled
                  />
                  <Input
                    type="text"
                    label="Bundle Group Precedence"
                    value={
                      watch('bundleConfigurations')?.bundleGroupPrecedence ??
                      '—'
                    }
                    disabled
                  />
                  <Input
                    type="text"
                    label="New Auto Apply"
                    value={watch('bundleConfigurations')?.newAutoApply || 'N'}
                    disabled
                  />
                  <Input
                    type="text"
                    label="Existing Auto Apply"
                    value={
                      watch('bundleConfigurations')?.existingAutoApply || 'N'
                    }
                    disabled
                  />
                </div>
                {(watch('bundleConfigurations')?.bundleConfig || []).length >
                  0 && (
                  <div style={{marginBottom: '8px'}}>
                    <p
                      style={{
                        fontFamily: FONT,
                        fontSize: '13px',
                        fontWeight: 500,
                        color: '#374151',
                        margin: '0 0 6px',
                      }}
                    >
                      Bundle Config Items
                    </p>
                    <div
                      style={{display: 'flex', flexWrap: 'wrap', gap: '8px'}}
                    >
                      {watch('bundleConfigurations').bundleConfig.map(
                        (cfg, i) => (
                          <span
                            key={i}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              background: '#f3f4f6',
                              border: '1px solid #d1d5db',
                              borderRadius: '999px',
                              padding: '4px 12px',
                              fontFamily: FONT,
                              fontSize: '13px',
                              color: '#374151',
                            }}
                          >
                            {cfg}
                          </span>
                        ),
                      )}
                    </div>
                  </div>
                )}
              </>
            )}

            <p style={sectionLabel}>Required Roles</p>
            <p
              style={{
                fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif",
                fontSize: '13px',
                color: '#6b7280',
                margin: '0 0 4px',
              }}
            >
              Coupon is accessible only to users with the below roles. If no
              roles are associated with the coupon, it is available to all
              users.
            </p>
            <PillTagList
              control={control}
              name="roles"
              fieldKey="roleName"
              options={allRoles.map(r => ({label: r, value: r}))}
              placeholder="Select role…"
            />

            {defaultFlag && (
              <>
                <p style={sectionLabel}>Compatible Plans</p>
                <p
                  style={{
                    fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif",
                    fontSize: '13px',
                    color: '#6b7280',
                    margin: '0 0 4px',
                  }}
                >
                  Coupon is available only for the below plans
                </p>
                <CompatiblePlansList control={control} name="compatiblePlans" />
              </>
            )}

            <p style={sectionLabel}>Incompatible Coupons</p>
            <p
              style={{
                fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif",
                fontSize: '13px',
                color: '#6b7280',
                margin: '0 0 4px',
              }}
            >
              Coupon is incompatible with below list of coupons
            </p>
            <PillTagList
              control={control}
              name="incompatibleCoupons"
              fieldKey="incompatibleCoupon"
              placeholder="Enter coupon code…"
            />

            <p style={sectionLabel}>Exclusion Dates</p>
            <p
              style={{
                fontFamily: "'Inter', 'Helvetica Neue', Arial, sans-serif",
                fontSize: '13px',
                color: '#6b7280',
                margin: '0 0 4px',
              }}
            >
              Coupon will not be available on the following dates
            </p>
            <PillTagList
              control={control}
              name="exclusionDates"
              fieldKey="date"
              type="date"
              min={TODAY}
              placeholder="Pick a date…"
            />
          </div>
        )}
        <Modal
          isOpen={confirmClose}
          onClose={() => setConfirmClose(false)}
          title="Cancel Changes"
          size="md"
          footer={
            <>
              <Button
                variant="secondary"
                style={ORANGE_SECONDARY}
                onClick={() => setConfirmClose(false)}
              >
                No
              </Button>
              <Button
                variant="primary"
                style={ORANGE_PRIMARY}
                onClick={() => {
                  setConfirmClose(false)
                  onClose()
                }}
              >
                Yes
              </Button>
            </>
          }
          footerAlign="right"
        >
          <p
            style={{
              fontFamily: FONT,
              margin: 0,
              fontSize: '14px',
              color: '#4a4a4a',
              whiteSpace: 'nowrap',
            }}
          >
            You have unsaved changes. Are you sure you want to leave?
          </p>
        </Modal>
      </div>
    </Modal>
  )
}

export {EditCouponModal}
